'use client';

import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/navigation';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { LeagueCard } from '@/components/molecules/LeaguesCard';
import { LeagueCardSkeleton } from '@/components/molecules/skeletons/LeagueCardSkeleton';
import { League } from '@/entities';
import { useGetRecommendedLeaguesQuery } from '@/hooks/league/useRecommendedLeaguesQuery';

interface RecommendedLeaguesProps {
	isHomepage?: boolean;
}

export const RecommendedLeagues = ({ isHomepage }: RecommendedLeaguesProps) => {
	const router = useRouter();
	const { data: recommendedLeagues, isFetching } = useGetRecommendedLeaguesQuery();

	const containerRef = useRef<HTMLDivElement>(null);
	const interval = useRef<NodeJS.Timeout | null>(null);

	const [isFirstItemVisible, setIsFirstItemVisible] = useState(true);
	const [isLastItemVisible, setIsLastItemVisible] = useState(false);

	const handleScroll = () => {
		const container = containerRef.current;

		if (container) {
			const isAtStart = container.scrollLeft === 0;
			const isAtEnd = container.scrollLeft + container.offsetWidth >= container.scrollWidth;

			setIsFirstItemVisible(isAtStart);
			setIsLastItemVisible(isAtEnd);
		}
	};

	useEffect(() => {
		const container = containerRef.current;
		if (container) {
			container.addEventListener('scroll', handleScroll);
		}
		return () => {
			if (container) {
				container.removeEventListener('scroll', handleScroll);
			}

			if (interval.current) {
				clearInterval(interval.current);
			}
		};
	}, []);

	const scroll = React.useCallback((direction: 'left' | 'right') => {
		const div = containerRef.current;
		if (div) {
			const position = direction === 'right' ? div.scrollLeft + 250 : div.scrollLeft - 250;
			const maxScrollLeft = div.scrollWidth - div.clientWidth;
			const left = direction === 'right' ? (position < maxScrollLeft ? position : maxScrollLeft) : position < 0 ? 0 : position;
			div.scrollTo({ left, behavior: 'smooth' });
		}
	}, []);

	if ((recommendedLeagues?.data?.length === 0 || recommendedLeagues?.statusCode !== 200) && !isFetching) {
		if (isHomepage) {
			router.replace('/search');
		}
		return null;
	}

	return (
		<div className="h-full bg-gray-100 py-10">
			<div className={`mx-auto flex flex-col justify-center ${isHomepage ? 'max-w-screen-2xl' : ''}`}>
				<>
					<div className="flex items-center justify-between px-4">
						<div className="flex flex-col gap-2">
							<h2 className="text-2xl font-semibold text-black">Recommended Leagues</h2>
							{/* <span className="text-sm font-normal leading-5 text-gray-500">100 mile radius from Istanbul, 34</span> */}
						</div>

						{/* {recommendedLeagues?.data?.length !== 0 && (
							<div className="cursor-pointer text-base font-semibold leading-6 text-experiment hover:underline">Show all</div>
						)} */}
					</div>

					<div className="relative">
						{!isFirstItemVisible && recommendedLeagues?.data?.length !== 0 && (
							<button
								type="button"
								className="absolute left-0 top-[35%] z-10 flex size-10 cursor-pointer items-center justify-center rounded-full border border-gray-500 bg-white/70 backdrop-blur-sm transition duration-150 hover:bg-white disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-white lg:size-14 2xl:-left-0"
								onClick={() => scroll('left')}
							>
								<FontAwesomeIcon icon={faArrowLeft} className="block text-base text-gray-500" />
							</button>
						)}

						{!isLastItemVisible && recommendedLeagues?.data?.length !== 0 && (
							<button
								type="button"
								className="absolute right-0 top-[35%] z-10 ml-4 flex size-10 cursor-pointer items-center justify-center rounded-full border border-gray-500 bg-white/70 backdrop-blur-sm transition duration-150 hover:bg-white disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-white lg:size-14 2xl:-right-0"
								onClick={() => scroll('right')}
							>
								<FontAwesomeIcon icon={faArrowRight} className="block text-base text-gray-500" />
							</button>
						)}

						<div className="no-scrollbar mt-6 flex snap-x snap-mandatory gap-6 overflow-x-auto px-4" ref={containerRef}>
							{isFetching ? (
								<LeagueCardSkeleton />
							) : (
								recommendedLeagues?.data?.map((league: League) => <LeagueCard isHeightFitContent key={league.uuid} league={league} />)
							)}
						</div>
					</div>
				</>
			</div>
		</div>
	);
};
